import { Base, variables } from '@bitrise/bitkit';
import {
  Viz,
  VizHTML,
  VizHTMLArtboard,
  VizSVG,
  VizSVGArea,
  VizSVGLine,
  VizSVGAxisX,
  VizSVGAxisY,
  SeriesPointXY,
  useGetNearestPoint,
} from '@bitrise/bitviz';
import { OverviewMetric } from '@trace/types';
import React, { FunctionComponent, PointerEvent, useState } from 'react';
import { useParams } from 'react-router';
import min from 'lodash.min';
import classNames from 'classnames';
import getFormattedValue from '../../../utils/metric/getFormattedValue';
import OverviewCard from '../OverviewCard/OverviewCard';
import OverviewTitle from '../OverviewCard/OverviewTitle';
import OverviewComparison from '../OverviewComparison/OverviewComparison';
import './OverviewCrashFreeSessions.css';
import OverviewTooltip from '../OverviewTooltip/OverviewTooltip';
import OverviewTooltipComparison from '../OverviewTooltip/OverviewTooltipComparison';
import getJoinedNullDataSet from '../../../utils/metric/getJoinedNullDataSet';
import { trackEvent } from '../../../utils/tracking';
import MessageFetching from '../../ViewVisualisations/MessageFetching';
import MessageFailing from '../../ViewVisualisations/MessageFailing';

export interface Props {
  data?: OverviewMetric;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
  xAxisLabels: [string, number][];
}

const OverviewCrashFreeSessions: FunctionComponent<Props> = ({
  data,
  isError,
  isFetching,
  refetch,
  xAxisLabels,
}) => {
  const { appId } = useParams<{ appId: string }>();
  const yMin = Math.floor(min(data?.app || []) || 0);
  const joinedDataSet = getJoinedNullDataSet(data?.app || [], {
    withEnd: true,
    withStart: true,
  });

  const [focusPoint, setFocusPoint] = useState<null | SeriesPointXY>(null);

  const backgroundClasses = classNames('OverviewCrashFreeSessions__background', {
    'OverviewCrashFreeSessions__background--with-stripes': !isError && !isFetching,
  });

  const getNearestPoint = useGetNearestPoint(
    data?.app.map((y, x) => ({ x, y })),
    { axis: 'x' },
  );

  const onPointerMove = (_: PointerEvent, point?: SeriesPointXY) => {
    if (point && point.x !== null) {
      const nearestPoint = getNearestPoint(point);

      if (nearestPoint && nearestPoint.x !== null) {
        setFocusPoint(nearestPoint);
        return;
      }
    }

    setFocusPoint(null);
  };

  const onCardButtonClick = () => {
    trackEvent('Overview.WidgetDetails', { widget: 'crashes' });
  };

  return (
    <OverviewCard
      toPath={`/${appId}/crashes`}
      toText="Crashes"
      onCardButtonClick={onCardButtonClick}
    >
      <OverviewTitle title="Crash-free sessions">
        <OverviewComparison align="end" data={data} isFetching={isFetching} unit="percent" />
      </OverviewTitle>

      <Viz
        height={200}
        paddingBottom={22}
        paddingLeft={34}
        paddingTop={22}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerMove}
        xDomain={[-1, ...xAxisLabels.map((_, x) => x), xAxisLabels.length]}
        xDomainScale="band"
        xDomainPaddingOuter={-0.5}
        yDomain={[yMin, 100]}
      >
        <VizHTML>
          <VizHTMLArtboard>
            <Base
              backgroundColor="gray-1"
              borderRadius="x2"
              className={backgroundClasses}
              height="100%"
              width="100%"
            />
          </VizHTMLArtboard>
        </VizHTML>

        <VizSVG>
          <defs>
            <linearGradient id="overviewCrashFreeGradientStart">
              <stop offset="0%" stopColor={variables.colorGray1} />
              <stop offset="100%" stopColor={variables.colorDarkblue1} />
            </linearGradient>

            <linearGradient id="overviewCrashFreeGradientEnd">
              <stop offset="0%" stopColor={variables.colorDarkblue1} />
              <stop offset="100%" stopColor={variables.colorGray1} />
            </linearGradient>
          </defs>

          <VizSVGAxisX
            fill={variables.colorGray6}
            ticksOnly
            tickLineLength={0.001}
            format={(x) => xAxisLabels[x] && xAxisLabels[x][0]}
          />

          <VizSVGAxisY
            fill={variables.colorGray6}
            flip
            format={(v) => getFormattedValue('percent', v)}
            tickLineLength={0.001}
            tickCount={2}
            ticksOnly
          />

          {data?.app && (
            <VizSVGArea
              animate
              curve="curveMonotoneX"
              fill={variables.colorDarkblue1}
              originY={yMin}
              series={data.app.map((y, x) => ({ x, y }))}
            />
          )}

          {data?.app && (
            <VizSVGArea
              animate
              curve="curveMonotoneX"
              fill={variables.colorDarkblue1}
              originY={yMin}
              series={joinedDataSet.map((y, x) => ({ x, y }))}
            />
          )}

          {data?.app && (
            <VizSVGArea
              animate
              clipToArtboard
              curve="curveMonotoneX"
              fill="url('#overviewCrashFreeGradientStart')"
              originY={yMin}
              series={[
                { x: -1, y: joinedDataSet[0] },
                { x: 0, y: joinedDataSet[0] },
              ]}
            />
          )}

          {data?.app && (
            <VizSVGArea
              animate
              clipToArtboard
              curve="curveMonotoneX"
              fill="url('#overviewCrashFreeGradientEnd')"
              originY={yMin}
              series={[
                { x: joinedDataSet.length - 1, y: joinedDataSet[joinedDataSet.length - 1] },
                { x: joinedDataSet.length, y: joinedDataSet[joinedDataSet.length - 1] },
              ]}
            />
          )}

          {data?.app && (
            <VizSVGLine
              animate
              curve="curveMonotoneX"
              originY={yMin}
              series={data.app.map((y, x) => ({ x, y }))}
              stroke={variables.colorDarkblue2}
              strokeWidth="2"
            />
          )}
        </VizSVG>

        <VizHTML>
          <VizHTMLArtboard>
            <MessageFetching fetching={isFetching} />
            <MessageFailing failing={isError} onFetch={refetch} />
          </VizHTMLArtboard>
        </VizHTML>

        {!!focusPoint && focusPoint.x !== null && (
          <OverviewTooltip point={focusPoint} timestamp={xAxisLabels[focusPoint.x][1]}>
            <OverviewTooltipComparison
              currValue={focusPoint.y}
              prevValue={data?.app[focusPoint.x - 1] || null}
              unit="percent"
            />
          </OverviewTooltip>
        )}
      </Viz>
    </OverviewCard>
  );
};

export default OverviewCrashFreeSessions;
